import { useState, useEffect, useCallback } from 'react';
import { Arquivo, FiltrosArquivo, Paginacao, ErroApi } from '../types/gerArquivos';
import { getLastTenDaysRange } from '../utils/date';
import { GerArquivosService } from '../services/gerArquivosService';

interface UseGerArquivosReturn {
  arquivos: Arquivo[];
  paginacao: Paginacao;
  filtros: FiltrosArquivo;
  carregando: boolean;
  erro: ErroApi | null;
  atualizarFiltros: (novosFiltros: Partial<FiltrosArquivo>) => void;
  alternarFavorito: (arquivoId: string) => Promise<void>;
  recarregarArquivos: () => Promise<void>;
  irParaPagina: (pagina: number) => void;
}

export function useGerArquivos(): UseGerArquivosReturn {
  const [arquivos, setArquivos] = useState<Arquivo[]>([]);
  const [paginacao, setPaginacao] = useState<Paginacao>({
    paginaAtual: 1,
    itensPorPagina: 10,
    totalItens: 0,
    totalPaginas: 0
  });
  // Datas padrão (últimos 10 dias)
  const defaultDates = getLastTenDaysRange();

  const [filtros, setFiltros] = useState<FiltrosArquivo>({
    searchTerm: '',
    dateFrom: defaultDates.from,
    dateTo: defaultDates.to,
    dateFilterEnabled: true,
    showAllUsers: false,
  });
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<ErroApi | null>(null);

  const buscarArquivos = useCallback(async () => {
    setCarregando(true);
    setErro(null);

    try {
      // Buscar dos dois sources em paralelo
      const [resPre, resSave] = await Promise.all([
        GerArquivosService.buscarArquivos(filtros, {
          paginaAtual: paginacao.paginaAtual,
          itensPorPagina: paginacao.itensPorPagina
        }, 'pre'),
        GerArquivosService.buscarArquivos(filtros, {
          paginaAtual: paginacao.paginaAtual,
          itensPorPagina: paginacao.itensPorPagina
        }, 'cdr_save').catch(() => null) // Se falhar, não bloqueia os pré-orçamentos
      ]);

      const preArquivos = resPre.success ? resPre.data.arquivos : [];
      const saveArquivos = resSave?.success ? resSave.data.arquivos : [];

      // Mergear e ordenar por data (mais recente primeiro)
      const merged = [...preArquivos, ...saveArquivos].sort((a, b) => {
        const da = new Date(a.dataCriacao).getTime();
        const db = new Date(b.dataCriacao).getTime();
        return db - da;
      });

      // Paginação unificada: usa o total de ambos
      const totalPre = resPre.success ? resPre.data.paginacao.totalItens : 0;
      const totalSave = resSave?.success ? resSave.data.paginacao.totalItens : 0;
      const totalItens = totalPre + totalSave;

      setArquivos(merged);
      setPaginacao(prev => ({
        ...prev,
        totalItens,
        totalPaginas: Math.ceil(totalItens / prev.itensPorPagina)
      }));
      try { (window as any).sendToTerminal?.('GERARQ:LIST_OK', { count: merged.length, pre: preArquivos.length, save: saveArquivos.length }); } catch {}
    } catch (error) {
      setErro({ codigo: 'API_UNAVAILABLE', mensagem: 'Erro de conexão com o servidor', detalhes: error instanceof Error ? error.message : 'Erro desconhecido' });
      try { (window as any).sendToTerminal?.('GERARQ:LIST_ERROR', { code: 'API_UNAVAILABLE' }); } catch {}
    } finally {
      setCarregando(false);
    }
  }, [filtros, paginacao.paginaAtual, paginacao.itensPorPagina]);

  const alternarFavorito = useCallback(async (arquivoId: string) => {
    try {
      const arquivo = arquivos.find(a => a.id === arquivoId);
      if (!arquivo) return;
      if (arquivo.source === 'cdr_save') return; // cdr_file_save não tem favorito

      const response = await GerArquivosService.alternarFavorito(arquivoId, !arquivo.favorito);

      if (response.success) {
        setArquivos(prev => prev.map(a =>
          a.id === arquivoId ? { ...a, favorito: !a.favorito } : a
        ));
        try { (window as any).sendToTerminal?.('GERARQ:FAVORITO_OK', { id: arquivoId }); } catch {}
      } else {
        setErro({ codigo: 'VALIDATION_ERROR', mensagem: 'Erro ao atualizar favorito' });
      }
    } catch {
      setErro({ codigo: 'API_UNAVAILABLE', mensagem: 'Erro ao atualizar favorito' });
    }
  }, [arquivos]);

  const atualizarFiltros = useCallback((novosFiltros: Partial<FiltrosArquivo>) => {
    setFiltros(prev => ({ ...prev, ...novosFiltros }));
    setPaginacao(prev => ({ ...prev, paginaAtual: 1 }));
  }, []);

  const recarregarArquivos = useCallback(async () => { await buscarArquivos(); }, [buscarArquivos]);
  const irParaPagina = useCallback((pagina: number) => { setPaginacao(prev => ({ ...prev, paginaAtual: pagina })); }, []);

  useEffect(() => { buscarArquivos(); }, [buscarArquivos]);

  return { arquivos, paginacao, filtros, carregando, erro, atualizarFiltros, alternarFavorito, recarregarArquivos, irParaPagina };
}
